// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

/**
 * Performance.now is not supported in opera mini
 */
export const perfNow = (): number => {
	try {
		// eslint-disable-next-line
		return performance.now();
	} catch {
		return 0;
	}
};

export const createPerformanceObserver = (
	site: string | string[],
	entryTypes: PerformanceEntry['entryType'][],
): {
	takeEntriesAndDisconnect: () => PerformanceEntry[];
} => {
	const entries: PerformanceEntry[] = [];

	const handleEntry = (entry: PerformanceEntry) => {
		if (entry.entryType === 'resource') {
			const url = new URL(entry.name);
			if (
				(typeof site === 'string' && url.host === site) ||
				(Array.isArray(site) && site.includes(url.host))
			) {
				entries.push(entry);
			}
		} else {
			entries.push(entry);
		}
	};

	let observer: PerformanceObserver | null = null;

	try {
		observer = new PerformanceObserver((entries) => {
			entries.getEntries().forEach((entry) => {
				handleEntry(entry);
			});
		});

		for (const entryType of entryTypes) {
			observer.observe({
				type: entryType,
				buffered: true,
			});
		}

		return {
			takeEntriesAndDisconnect: () => {
				if (observer) {
					const records = observer.takeRecords();
					for (const record of records) {
						handleEntry(record);
					}
					observer.disconnect();
				}
				return entries;
			},
		};
	} catch {
		// If observer was created but observe() failed, clean it up
		if (observer) {
			try {
				observer.disconnect();
			} catch {
				// Ignore errors during cleanup
			}
		}

		// Fallback for browsers (Safari/WebKit) that don't support this pattern
		// Return a no-op implementation that returns an empty array
		return {
			takeEntriesAndDisconnect: () => [],
		};
	}
};

export const performanceAttributes = (
	entry: PerformanceEntry,
):
	| {
			resource: string;
			duration: number;
			entryType: string;
			startTime: number;
	  }
	| {
			tcpHandshakeDuration: number;
			domainLookupDuration: number;
			redirectionDuration: number;
			requestDuration: number;
			tlsNegotiationDuration: number;
			fetchDuration: number;
			resource: string;
			duration: number;
			entryType: string;
			startTime: number;
	  } => {
	const baseAttributes = {
		resource: entry.name,
		duration: entry.duration,
		entryType: entry.entryType,
		startTime: entry.startTime,
	};

	if (entry.entryType === 'resource') {
		const resourceEntry = entry as PerformanceResourceTiming;
		return {
			...baseAttributes,
			tcpHandshakeDuration: resourceEntry.connectEnd - resourceEntry.connectStart,
			domainLookupDuration: resourceEntry.domainLookupEnd - resourceEntry.domainLookupStart,
			redirectionDuration: resourceEntry.redirectEnd - resourceEntry.redirectStart,
			requestDuration: resourceEntry.responseStart - resourceEntry.requestStart,
			tlsNegotiationDuration: resourceEntry.requestStart - resourceEntry.secureConnectionStart,
			fetchDuration: resourceEntry.responseEnd - resourceEntry.fetchStart,
		};
	}

	return baseAttributes;
};

export type ScriptResourceTimingPhases = {
	scriptStalledStartTime?: number;
	scriptStalledEndTime?: number;
	scriptDnsStartTime?: number;
	scriptDnsEndTime?: number;
	scriptConnectStartTime?: number;
	scriptConnectEndTime?: number;
	scriptWaitStartTime?: number;
	scriptWaitEndTime?: number;
	scriptDownloadStartTime?: number;
	scriptDownloadEndTime?: number;
};

const isPerformanceResourceTiming = (entry: PerformanceEntry): entry is PerformanceResourceTiming =>
	entry.entryType === 'resource';

const hasDetailedResourceTiming = (entry: PerformanceResourceTiming): boolean => {
	// Cross-origin without Timing-Allow-Origin zeroes responseStart while startTime remains set.
	return !(entry.responseStart === 0 && entry.startTime > 0);
};

const toPhase = (start: number, end: number): { startTime: number; endTime: number } | undefined =>
	end > start ? { startTime: start, endTime: end } : undefined;

const firstPositive = (...values: number[]): number => values.find((value) => value > 0) ?? 0;

/**
 * Splits a script resource entry into UFO-ready phases. Zero-duration or TAO-hidden
 * timestamps are omitted so reused connections do not emit empty segments.
 */
export const getScriptResourceTimingPhases = (
	entry: PerformanceEntry,
): ScriptResourceTimingPhases => {
	if (!isPerformanceResourceTiming(entry)) {
		return {};
	}

	if (!hasDetailedResourceTiming(entry)) {
		return {};
	}

	const stalledEnd = firstPositive(entry.domainLookupStart, entry.connectStart, entry.requestStart);
	const stalled = toPhase(entry.fetchStart, stalledEnd);
	const dns = toPhase(entry.domainLookupStart, entry.domainLookupEnd);
	const connect = toPhase(entry.connectStart, entry.connectEnd);
	const wait = toPhase(entry.requestStart, entry.responseStart);
	const download = toPhase(entry.responseStart, entry.responseEnd);

	return {
		...(stalled && {
			scriptStalledStartTime: stalled.startTime,
			scriptStalledEndTime: stalled.endTime,
		}),
		...(dns && {
			scriptDnsStartTime: dns.startTime,
			scriptDnsEndTime: dns.endTime,
		}),
		...(connect && {
			scriptConnectStartTime: connect.startTime,
			scriptConnectEndTime: connect.endTime,
		}),
		...(wait && {
			scriptWaitStartTime: wait.startTime,
			scriptWaitEndTime: wait.endTime,
		}),
		...(download && {
			scriptDownloadStartTime: download.startTime,
			scriptDownloadEndTime: download.endTime,
		}),
	};
};

const STAGING_ANALYTICS_URL = 'https://as.staging.atl-paas.net/api/v1/batch';
const PROD_ANALYTICS_URL = 'https://as.atlassian.com/api/v1/batch';

export const sendPerformanceEntries = (
	entries: PerformanceEntry[],
	analyticsContext?: Record<string, unknown> | undefined,
): void => {
	const destination = process.env.CAWI_ENV === 'prod' ? PROD_ANALYTICS_URL : STAGING_ANALYTICS_URL;

	fetch(destination, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			batch: entries.slice(0, 10).map((entry) => ({
				type: 'track',
				event: 'performance measured',
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				messageId: uuidv4(),
				timestamp: new Date().toISOString(),
				sentAt: new Date().toISOString(),
				properties: {
					eventType: 'operational',
					env: process.env.CAWI_ENV || 'staging',
					source: 'script',
					tags: [],
					origin: 'web',
					platform: 'web',
					product: 'csm',
					subproduct: 'widget',
					action: 'measured',
					actionSubject: 'performance',
					attributes: {
						entry: entry.toJSON(),
						...performanceAttributes(entry),
						...(analyticsContext?.attributes ?? {}),
					},
				},
			})),
			context: {
				validationMode: 'complete',
			},
			sentAt: new Date().toISOString(),
		}),
	});
};
